// theme.js — the survey-sheet world of DESIGN.md, for the client portal.
// primary = the brand action ink (what can be clicked), secondary = neutral
// graphite. A customer's userConfiguration palette still overrides both
// (common/root/AppShell/QueryPages.jsx).
import { createMuiTheme, responsiveFontSizes } from '@material-ui/core/styles'
import { darkShadows } from '@zeliot/common/constants/shadows'
import { SHARED, SHARED_DARK, resolveBrand } from './brands'

const brand = resolveBrand()

const FONT = [
  '"Public Sans"',
  '-apple-system',
  '"Segoe UI"',
  'Roboto',
  'Arial',
  'sans-serif'
].join(',')

// Soft, offset shadows: only floating layers (menus, dialogs, popovers) lift
const shadow = (y, blur, a) => `0 ${y}px ${blur}px rgba(29, 42, 34, ${a})`
const lightShadows = ['none'].concat(
  Array.from({ length: 24 }, (_, i) => {
    const level = i + 1
    return `${shadow(1, 2, 0.06)}, ${shadow(Math.ceil(level / 2), level * 2, 0.08)}`
  })
)

// Exposes the brand to plain CSS (index.css) as custom properties
const root = document.documentElement.style
root.setProperty('--brand-rail', brand.rail)
root.setProperty('--brand-action', brand.action)
root.setProperty('--brand-action-tint', brand.actionTint)
root.setProperty('--ground', SHARED.ground)
root.setProperty('--ink', SHARED.ink)
root.setProperty('--rule-strong', SHARED.ruleStrong)

const typography = {
  fontSize: 12,
  fontFamily: FONT,
  h1: { fontWeight: 700, fontSize: '2rem', letterSpacing: '-0.015em' },
  h2: { fontWeight: 700, fontSize: '1.5rem', letterSpacing: '-0.01em' },
  h3: { fontWeight: 700, fontSize: '1.375rem' },
  h4: { fontWeight: 700, fontSize: '1.25rem' },
  h5: { fontWeight: 600, fontSize: '1.0625rem' },
  h6: { fontWeight: 600, fontSize: '0.9375rem' },
  subtitle1: { fontWeight: 500 },
  subtitle2: { fontWeight: 600 },
  // DESIGN.md Density Floor Rule: body text is 13px
  body1: { fontSize: '0.8125rem' },
  button: { textTransform: 'none', fontWeight: 600 },
  caption: { letterSpacing: 0 }
}

const overrides = ink => ({
  MuiButton: {
    root: { borderRadius: 6 },
    contained: { boxShadow: 'none', '&:hover': { boxShadow: 'none' }, '&:active': { boxShadow: 'none' } }
  },
  MuiPaper: { rounded: { borderRadius: 6 } },
  MuiCard: { root: { boxShadow: 'none', border: `1px solid ${ink.rule}` } },
  MuiAppBar: { root: { boxShadow: 'none' } },
  MuiTableRow: { root: { height: 36 }, head: { height: 36 } },
  MuiTableCell: {
    // DESIGN.md tables: 6px cell padding, 12px column heads that wrap
    root: { padding: '0 6px', borderBottom: `1px solid ${ink.rule}`, fontVariantNumeric: 'tabular-nums' },
    head: { fontWeight: 600, fontSize: '0.75rem', lineHeight: 1.25, color: ink.inkMuted }
  },
  MuiTab: { root: { textTransform: 'none', fontWeight: 600 } },
  MuiTooltip: { tooltip: { fontSize: '0.75rem' } },
  MuiListSubheader: {
    root: { fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }
  }
})

// Base light theme options
const baseLightOptions = {
  palette: {
    type: 'light',
    primary: {
      main: brand.action,
      light: brand.actionTint,
      dark: brand.actionDark,
      contrastText: '#FFFFFF'
    },
    secondary: {
      main: SHARED.state.neutral,
      light: '#8A948E',
      dark: '#3F4A43'
    },
    error: { main: SHARED.state.critical },
    warning: { main: SHARED.state.warning },
    success: { main: SHARED.state.good },
    text: { primary: SHARED.ink, secondary: SHARED.inkMuted },
    divider: SHARED.rule,
    background: {
      default: SHARED.ground,
      paper: SHARED.surface
    },
    link: {
      main: brand.action
    }
  },
  shape: { borderRadius: 6 },
  shadows: lightShadows,
  typography,
  overrides: overrides(SHARED),
  brand
}

// Default light theme
let defaultTheme = createMuiTheme({
  ...baseLightOptions
})
defaultTheme = responsiveFontSizes(defaultTheme)

// Dark theme: the same world at night, inks lifted for the dark grounds
const darkPalette = {
  type: 'dark',
  primary: {
    main: brand.actionOnDark,
    light: brand.actionOnDark,
    dark: brand.action,
    contrastText: '#0F1511'
  },
  secondary: {
    main: SHARED_DARK.state.neutral,
    light: '#C2CBC4',
    dark: '#5F6B64'
  },
  error: { main: SHARED_DARK.state.critical },
  warning: { main: SHARED_DARK.state.warning },
  success: { main: SHARED_DARK.state.good },
  text: { primary: SHARED_DARK.ink, secondary: SHARED_DARK.inkMuted },
  divider: SHARED_DARK.rule,
  background: {
    default: SHARED_DARK.ground,
    paper: SHARED_DARK.surface
  },
  link: { main: brand.actionOnDark }
}

let darkTheme = createMuiTheme({
  palette: darkPalette,
  shape: { borderRadius: 6 },
  typography,
  overrides: overrides(SHARED_DARK),
  shadows: darkShadows,
  brand
})
darkTheme = responsiveFontSizes(darkTheme)

// Non-whitelisted themes (light & dark) without responsive font sizing
const nonWlDefaultTheme = {
  ...baseLightOptions
}

const nonWlDarkTheme = createMuiTheme({
  palette: darkTheme.palette,
  typography: darkTheme.typography,
  shadows: darkShadows,
  brand
})

export { defaultTheme, darkTheme, nonWlDefaultTheme, nonWlDarkTheme }
